import { makeRng } from '../../utils/makeRng';
import { isWalkable } from '../map/isWalkable';
import {
  SPIDER_BOSS_ENEMY_ID,
  SPIDER_BOSS_FLOOR,
  SPIDER_BOSS_MAP_SIZE,
  getSpiderBossFloorMood,
} from './spiderBossConfig';

export const SPECIAL_BOSS_FLOOR = SPIDER_BOSS_FLOOR;
export const SECOND_SPECIAL_BOSS_FLOOR = 9;
export const THIRD_SPECIAL_BOSS_FLOOR = 10;
export const FOURTH_SPECIAL_BOSS_FLOOR = 11;
export const EMPOWERED_SPECIAL_BOSS_FLOOR = SECOND_SPECIAL_BOSS_FLOOR;
export const EMPOWERED_SPECIAL_BOSS_FLOORS = Object.freeze([
  SECOND_SPECIAL_BOSS_FLOOR,
  THIRD_SPECIAL_BOSS_FLOOR,
  FOURTH_SPECIAL_BOSS_FLOOR,
]);
export const SPECIAL_BOSS_FLOORS = Object.freeze([
  SPECIAL_BOSS_FLOOR,
  ...EMPOWERED_SPECIAL_BOSS_FLOORS,
]);
export const SPECIAL_BOSS_MAP_SIZE = SPIDER_BOSS_MAP_SIZE;
export const EMPOWERED_SPECIAL_BOSS_HP_MULTIPLIER = 1.5;
export const EMPOWERED_SPECIAL_BOSS_ARMOR_MULTIPLIER = 1.5;
export const MONSIGNOR_BOSS_ENEMY_ID = 'monsignor';
export const MONSIGNOR_GUARD_ENEMY_ID = 'chained-penitent';
export const MONSIGNOR_LIBERATED_GUARD_TILE = 'chained-penitent-liberated';
export const MONSIGNOR_BOSS_HP_REDUCTION_PER_GUARD = 50;
export const MONSIGNOR_LIBERATED_PENITENT_LORE = `“With the fall of the Monsignor and the sundering of their chains, the Penitents now wander unbound. They drift around in confusion, dazed as men roused from a long and grievous dream, as though some ancient weight had at last been lifted from their shoulders. And yet, a lingering malice abides within their hollow stare and bared fangs, as though they still belonged to some greater design—some darker and more intricate will. As though they were not merely within the tower, but of it.”`;

export const PRIME_VESSEL_BOSS_ENEMY_ID = 'prime_vessel';
export const BLACKHAND_BOSS_ENEMY_ID = 'blackhand';
export const LABORATORY_FLOOR_ROAMING_ENEMY_ID = 'chymist';
export const BLACKHAND_FIXED_ZOOM_TORCH_PROGRESS = 0.4;
export const BLACKHAND_CONTRACT_LORE = `Target: Hooded Intruder
Priority: Absolute
Classification: Restricted
Authority: Withheld under seal
Advance: One half upon acceptance
Balance: One half upon presentation of proof of death
Conditions: No proclamation. No spectacle. No surviving witness.

<em>Catalog Addendum: Patron field left blank. Hand consistent with private Tower records.</em>`;
export const PRIME_VESSEL_DEVICE_HP_REDUCTION = 125;
export const PRIME_VESSEL_DEVICE_ENEMY_IDS = Object.freeze({
  primeCrucible: 'prime_crucible_device',
  pulseEngine: 'pulse_engine_device',
  synapticRetort: 'synaptic_retort_device',
  astralReliquary: 'astral_reliquary_device',
});

const SPECIAL_BOSS_WEIGHT_FALLBACK = 1;

const MONSIGNOR_FLOOR_WIDTH = 17;
const MONSIGNOR_FLOOR_HEIGHT = 17;
const MONSIGNOR_PLAYER_SPAWN = Object.freeze({ x: 8, y: 15 });
export const MONSIGNOR_BOSS_POS = Object.freeze({ x: 8, y: 4 });
export const MONSIGNOR_GUARD_COORDS = Object.freeze([
  { x: 6, y: 3 },
  { x: 10, y: 3 },
  { x: 5, y: 4 },
  { x: 11, y: 4 },
  { x: 6, y: 5 },
  { x: 10, y: 5 },
]);
const MONSIGNOR_LIT_BRAZIER_COORDS = Object.freeze([
  { x: 6, y: 2 },
  { x: 10, y: 2 },
  { x: 5, y: 3 },
  { x: 11, y: 3 },
  { x: 5, y: 5 },
  { x: 11, y: 5 },
]);
const MONSIGNOR_ROOM_CHECKS = Object.freeze([
  { x: 8, y: 15, allowed: ['steps-up'] },
  { x: 8, y: 7, allowed: ['door-open'] },
  { x: 6, y: 12, allowed: ['door-open'] },
  { x: 10, y: 12, allowed: ['door-open'] },
  { x: 3, y: 12, allowed: ['rest'] },
  { x: 13, y: 12, allowed: ['merchant'] },
  { x: 1, y: 11, allowed: ['bookshelf-a', 'bookshelf-b'] },
  { x: 15, y: 11, allowed: ['bookshelf-a', 'bookshelf-b'] },
]);

const PRIME_VESSEL_FLOOR_WIDTH = 31;
const PRIME_VESSEL_FLOOR_HEIGHT = 31;
const PRIME_VESSEL_PLAYER_SPAWN = Object.freeze({ x: 15, y: 22 });
const BLACKHAND_FLOOR_WIDTH = 21;
const BLACKHAND_FLOOR_HEIGHT = 43;
const BLACKHAND_PLAYER_SPAWN = Object.freeze({ x: 10, y: 1 });
export const BLACKHAND_CHEST_POS = Object.freeze({ x: 10, y: 41 });
export const BLACKHAND_BOSS_AMBUSH_POS = Object.freeze({ x: 10, y: 36 });
const BLACKHAND_ROOM_CHECKS = Object.freeze([
  { x: 10, y: 1, allowed: ['steps-up'] },
  { x: 3, y: 9, allowed: ['merchant'] },
  { x: 3, y: 13, allowed: ['brazier'] },
  { x: 9, y: 10, allowed: ['brazier'] },
  { x: 9, y: 12, allowed: ['brazier'] },
  { x: 17, y: 19, allowed: ['brazier'] },
  { x: 17, y: 23, allowed: ['rest'] },
  { x: 11, y: 20, allowed: ['brazier'] },
  { x: 11, y: 22, allowed: ['brazier'] },
  { x: 9, y: 31, allowed: ['brazier'] },
  { x: 11, y: 31, allowed: ['brazier'] },
  { x: 6, y: 11, allowed: ['corridor'] },
  { x: 14, y: 21, allowed: ['corridor'] },
  { x: 10, y: 31, allowed: ['corridor'] },
  { x: 10, y: 32, allowed: ['corridor'] },
  { x: 10, y: 36, allowed: ['corridor', 'enemy-boss', 'steps-down'] },
  { x: 10, y: 39, allowed: ['trap', 'corridor'] },
  { x: 10, y: 40, allowed: ['trap', 'corridor'] },
  { x: 10, y: 41, allowed: ['chest-closed', 'chest-open'] },
]);
export const PRIME_VESSEL_BOSS_POS = Object.freeze({ x: 15, y: 15 });
export const PRIME_VESSEL_DEVICE_COORDS = Object.freeze([
  { x: 3, y: 3, enemyId: PRIME_VESSEL_DEVICE_ENEMY_IDS.primeCrucible },
  { x: 27, y: 3, enemyId: PRIME_VESSEL_DEVICE_ENEMY_IDS.pulseEngine },
  { x: 3, y: 27, enemyId: PRIME_VESSEL_DEVICE_ENEMY_IDS.synapticRetort },
  { x: 27, y: 27, enemyId: PRIME_VESSEL_DEVICE_ENEMY_IDS.astralReliquary },
]);
const PRIME_VESSEL_CONNECTOR_DEFS = Object.freeze([
  {
    roomBounds: { x1: 1, y1: 1, x2: 5, y2: 5 },
    bounds: { x1: 1, y1: 6, x2: 12, y2: 12 },
    start: { x: 3, y: 5 },
    end: { x: 6, y: 13 },
  },
  {
    roomBounds: { x1: 25, y1: 1, x2: 29, y2: 5 },
    bounds: { x1: 18, y1: 6, x2: 29, y2: 12 },
    start: { x: 27, y: 5 },
    end: { x: 24, y: 13 },
  },
  {
    roomBounds: { x1: 1, y1: 25, x2: 5, y2: 29 },
    bounds: { x1: 1, y1: 18, x2: 12, y2: 24 },
    start: { x: 3, y: 25 },
    end: { x: 6, y: 17 },
  },
  {
    roomBounds: { x1: 25, y1: 25, x2: 29, y2: 29 },
    bounds: { x1: 18, y1: 18, x2: 29, y2: 24 },
    start: { x: 27, y: 25 },
    end: { x: 24, y: 17 },
  },
]);

const PRIME_VESSEL_LAB_TILE_IDS = Object.freeze({
  coreNorth: 'corridor_lab0b',
  coreSouth: 'corridor_lab0a',
  coreWest: 'corridor_lab0c',
  coreEast: 'corridor_lab0d',
  set1Top: 'corridor_lab1a',
  set1Bottom: 'corridor_lab1b',
  set2Center: 'corridor_lab2a',
  set2South: 'corridor_lab2b',
  set2East: 'corridor_lab2c',
  set2West: 'corridor_lab2d',
  set3South: 'corridor_lab3a',
  set3Center: 'corridor_lab3b',
  set3North: 'corridor_lab3c',
  set3East: 'corridor_lab3d',
  set3West: 'corridor_lab3e',
});

const PRIME_VESSEL_CORE_LAB_ART = Object.freeze([
  { x: PRIME_VESSEL_BOSS_POS.x, y: PRIME_VESSEL_BOSS_POS.y - 1, tile: PRIME_VESSEL_LAB_TILE_IDS.coreNorth },
  { x: PRIME_VESSEL_BOSS_POS.x, y: PRIME_VESSEL_BOSS_POS.y + 1, tile: PRIME_VESSEL_LAB_TILE_IDS.coreSouth },
  { x: PRIME_VESSEL_BOSS_POS.x - 1, y: PRIME_VESSEL_BOSS_POS.y, tile: PRIME_VESSEL_LAB_TILE_IDS.coreWest },
  { x: PRIME_VESSEL_BOSS_POS.x + 1, y: PRIME_VESSEL_BOSS_POS.y, tile: PRIME_VESSEL_LAB_TILE_IDS.coreEast },
]);

const PRIME_VESSEL_LAB_ART_CHECKS = Object.freeze(
  PRIME_VESSEL_CORE_LAB_ART.map(({ x, y, tile }) => ({ x, y, allowed: [tile] })),
);

const PRIME_VESSEL_LAB_SETPIECE_TEMPLATES = Object.freeze([
  {
    id: 'lab-setpiece-1',
    offsets: Object.freeze([
      { dx: 0, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set1Top },
      { dx: 0, dy: 1, tile: PRIME_VESSEL_LAB_TILE_IDS.set1Bottom },
    ]),
  },
  {
    id: 'lab-setpiece-2',
    offsets: Object.freeze([
      { dx: 0, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set2Center },
      { dx: 0, dy: 1, tile: PRIME_VESSEL_LAB_TILE_IDS.set2South },
      { dx: 1, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set2East },
      { dx: -1, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set2West },
    ]),
  },
  {
    id: 'lab-setpiece-3',
    offsets: Object.freeze([
      { dx: 0, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set3Center },
      { dx: 0, dy: 1, tile: PRIME_VESSEL_LAB_TILE_IDS.set3South },
      { dx: 0, dy: -1, tile: PRIME_VESSEL_LAB_TILE_IDS.set3North },
      { dx: 1, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set3East },
      { dx: -1, dy: 0, tile: PRIME_VESSEL_LAB_TILE_IDS.set3West },
    ]),
  },
]);

const PRIME_VESSEL_ROOM_CHECKS = Object.freeze([
  ...PRIME_VESSEL_LAB_ART_CHECKS,
  { x: 15, y: 22, allowed: ['steps-up'] },
  // The Prime Vessel arena is persistent. Once the player destroys a device,
  // that tile should stay gone instead of causing the whole laboratory map to
  // be regenerated back to its pristine state. Likewise, after the boss dies,
  // the central tile can legitimately become steps-down.
  { x: 15, y: 15, allowed: ['enemy-boss', 'steps-down'] },
  { x: 3, y: 3, allowed: ['enemy', 'floor'] },
  { x: 27, y: 3, allowed: ['enemy', 'floor'] },
  { x: 3, y: 27, allowed: ['enemy', 'floor'] },
  { x: 27, y: 27, allowed: ['enemy', 'floor'] },
  { x: 6, y: 13, allowed: ['corridor', 'floor'] },
  { x: 24, y: 13, allowed: ['corridor', 'floor'] },
  { x: 6, y: 17, allowed: ['corridor', 'floor'] },
  { x: 24, y: 17, allowed: ['corridor', 'floor'] },
  { x: 13, y: 13, allowed: ['door-open'] },
  { x: 17, y: 13, allowed: ['door-open'] },
  { x: 13, y: 17, allowed: ['door-open'] },
  { x: 17, y: 17, allowed: ['door-open'] },
  { x: 14, y: 20, allowed: ['merchant'] },
  { x: 16, y: 20, allowed: ['rest'] },
]);

function coordKey(x, y) {
  return `${Math.floor(Number(x) || 0)},${Math.floor(Number(y) || 0)}`;
}

const MONSIGNOR_GUARD_KEYS = new Set(
  MONSIGNOR_GUARD_COORDS.map(({ x, y }) => coordKey(x, y)),
);

const MONSIGNOR_LIT_BRAZIER_KEYS = new Set(
  MONSIGNOR_LIT_BRAZIER_COORDS.map(({ x, y }) => coordKey(x, y)),
);

const PRIME_VESSEL_DEVICE_ID_BY_KEY = new Map(
  PRIME_VESSEL_DEVICE_COORDS.map(({ x, y, enemyId }) => [coordKey(x, y), enemyId]),
);



function fillRect(map, x1, y1, x2, y2, tile) {
  for (let y = y1; y <= y2; y++) {
    for (let x = x1; x <= x2; x++) {
      if (map[y]?.[x] != null) map[y][x] = tile;
    }
  }
}

function setTile(map, x, y, tile) {
  if (map[y]?.[x] != null) map[y][x] = tile;
}

function isPrimeVesselLabArtTile(tile) {
  return typeof tile === 'string' && tile.startsWith('corridor_lab');
}

function isPrimeVesselLabPaintableTile(tile) {
  return tile === 'floor' || tile === 'corridor' || isPrimeVesselLabArtTile(tile);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function manhattan(a, b) {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function pick(rng, arr) {
  return Array.isArray(arr) && arr.length ? arr[Math.floor(rng() * arr.length)] : null;
}

function carveRectExclusive(map, x, y, w, h, tile) {
  for (let yy = y; yy < y + h; yy += 1) {
    for (let xx = x; xx < x + w; xx += 1) {
      if (map[yy]?.[xx] != null) map[yy][xx] = tile;
    }
  }
}

function carveOrthogonalPath(map, a, b, tile = 'corridor') {
  let x = a.x;
  let y = a.y;

  const carve = (cx, cy) => {
    if (map[cy]?.[cx] == null) return;
    if (map[cy][cx] === 'non-walkable') map[cy][cx] = tile;
  };

  const horizontalFirst = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);

  if (horizontalFirst) {
    const dx = Math.sign(b.x - x);
    while (x !== b.x) {
      carve(x, y);
      x += dx;
    }
    const dy = Math.sign(b.y - y);
    while (y !== b.y) {
      carve(x, y);
      y += dy;
    }
  } else {
    const dy = Math.sign(b.y - y);
    while (y !== b.y) {
      carve(x, y);
      y += dy;
    }
    const dx = Math.sign(b.x - x);
    while (x !== b.x) {
      carve(x, y);
      x += dx;
    }
  }

  carve(b.x, b.y);
}

function buildMonsignorBossFloorMap() {
  const map = Array.from({ length: MONSIGNOR_FLOOR_HEIGHT }, () =>
    Array(MONSIGNOR_FLOOR_WIDTH).fill('non-walkable'),
  );

  fillRect(map, 4, 1, 12, 7, 'floor');
  fillRect(map, 1, 11, 5, 13, 'floor');
  fillRect(map, 11, 11, 15, 13, 'floor');
  fillRect(map, 7, 12, 9, 12, 'floor');
  fillRect(map, 8, 8, 8, 15, 'floor');

  setTile(map, 8, 15, 'steps-up');
  setTile(map, 8, 7, 'door-open');
  setTile(map, 6, 12, 'door-open');
  setTile(map, 10, 12, 'door-open');

  setTile(map, 8, 2, 'chest-closed');
  setTile(map, MONSIGNOR_BOSS_POS.x, MONSIGNOR_BOSS_POS.y, 'enemy-boss');

  MONSIGNOR_GUARD_COORDS.forEach(({ x, y }) => setTile(map, x, y, 'enemy'));
  MONSIGNOR_LIT_BRAZIER_COORDS.forEach(({ x, y }) => setTile(map, x, y, 'brazier'));

  setTile(map, 1, 11, 'bookshelf-a');
  setTile(map, 5, 11, 'bookshelf-b');
  setTile(map, 1, 12, 'bookshelf-b');
  setTile(map, 1, 13, 'bookshelf-a');
  setTile(map, 5, 13, 'bookshelf-a');
  setTile(map, 3, 12, 'rest');

  setTile(map, 11, 11, 'bookshelf-a');
  setTile(map, 15, 11, 'bookshelf-b');
  setTile(map, 15, 12, 'bookshelf-a');
  setTile(map, 11, 13, 'bookshelf-b');
  setTile(map, 15, 13, 'bookshelf-a');
  setTile(map, 13, 12, 'merchant');

  return map;
}

function isMonsignorBossFloorMap(map) {
  if (!Array.isArray(map) || map.length !== MONSIGNOR_FLOOR_HEIGHT) return false;
  if (!Array.isArray(map[0]) || map[0].length !== MONSIGNOR_FLOOR_WIDTH) return false;

  return MONSIGNOR_ROOM_CHECKS.every(({ x, y, allowed }) => allowed.includes(map?.[y]?.[x]));
}

function pickRoomCenterWithinBounds(rng, bounds, existing) {
  const roomW = 2 + Math.floor(rng() * 3);
  const roomH = 2 + Math.floor(rng() * 3);
  const maxX = Math.max(bounds.x1, bounds.x2 - roomW + 1);
  const maxY = Math.max(bounds.y1, bounds.y2 - roomH + 1);
  const roomX = bounds.x1 + Math.floor(rng() * Math.max(1, maxX - bounds.x1 + 1));
  const roomY = bounds.y1 + Math.floor(rng() * Math.max(1, maxY - bounds.y1 + 1));
  const room = { x: roomX, y: roomY, w: roomW, h: roomH };

  const overlaps = existing.some((other) => {
    return !(
      room.x + room.w + 1 <= other.x ||
      other.x + other.w + 1 <= room.x ||
      room.y + room.h + 1 <= other.y ||
      other.y + other.h + 1 <= room.y
    );
  });

  if (overlaps) return null;

  return {
    room,
    center: {
      x: room.x + Math.floor(room.w / 2),
      y: room.y + Math.floor(room.h / 2),
    },
  };
}

function collectPrimeConnectorCandidates(map, bounds, occupied, allowedTiles = ['floor', 'corridor']) {
  const allowed = new Set(Array.isArray(allowedTiles) ? allowedTiles : ['floor', 'corridor']);
  const candidates = [];

  for (let y = bounds.y1; y <= bounds.y2; y += 1) {
    for (let x = bounds.x1; x <= bounds.x2; x += 1) {
      const tile = map[y]?.[x];
      if (!allowed.has(tile)) continue;
      const key = coordKey(x, y);
      if (occupied.has(key)) continue;
      candidates.push({ x, y });
    }
  }

  return candidates;
}

function takeRandomPrimeConnectorCandidate(rng, candidates) {
  if (!Array.isArray(candidates) || !candidates.length) return null;
  const index = Math.floor(rng() * candidates.length);
  const [chosen] = candidates.splice(index, 1);
  return chosen || null;
}

function placePrimeConnectorDecor(map, rng, bounds, occupied) {
  const pool = ['bookshelf-a', 'bookshelf-b', 'brazier', 'trap'];
  const candidates = collectPrimeConnectorCandidates(map, bounds, occupied, ['floor', 'corridor']);

  const count = Math.min(candidates.length, 1 + Math.floor(rng() * 2));
  for (let i = 0; i < count; i += 1) {
    const chosen = takeRandomPrimeConnectorCandidate(rng, candidates);
    if (!chosen) break;
    setTile(map, chosen.x, chosen.y, pool[Math.floor(rng() * pool.length)]);
    occupied.add(coordKey(chosen.x, chosen.y));
  }
}

function placePrimeConnectorBraziers(map, rng, bounds, occupied) {
  const candidates = collectPrimeConnectorCandidates(map, bounds, occupied, ['floor', 'corridor']);
  const count = Math.min(candidates.length, 2 + Math.floor(rng() * 2));

  for (let i = 0; i < count; i += 1) {
    const chosen = takeRandomPrimeConnectorCandidate(rng, candidates);
    if (!chosen) break;
    setTile(map, chosen.x, chosen.y, 'brazier');
    occupied.add(coordKey(chosen.x, chosen.y));
  }
}

function placePrimeConnectorRewardChest(map, rng, def, occupied) {
  const quadrantBounds = {
    x1: Math.min(def.roomBounds.x1, def.bounds.x1),
    y1: Math.min(def.roomBounds.y1, def.bounds.y1),
    x2: Math.max(def.roomBounds.x2, def.bounds.x2),
    y2: Math.max(def.roomBounds.y2, def.bounds.y2),
  };

  const floorCandidates = collectPrimeConnectorCandidates(map, quadrantBounds, occupied, ['floor']);
  const corridorCandidates = collectPrimeConnectorCandidates(map, quadrantBounds, occupied, ['corridor']);
  const candidates = floorCandidates.length ? floorCandidates : corridorCandidates;
  const chosen = takeRandomPrimeConnectorCandidate(rng, candidates);
  if (!chosen) return;

  setTile(map, chosen.x, chosen.y, 'chest-simple-closed');
  occupied.add(coordKey(chosen.x, chosen.y));
}


function collectPrimeLabSetpieceCandidates(map, bounds, template, reserved) {
  const candidates = [];

  for (let y = bounds.y1; y <= bounds.y2; y += 1) {
    for (let x = bounds.x1; x <= bounds.x2; x += 1) {
      const placements = template.offsets.map(({ dx, dy, tile }) => ({
        x: x + dx,
        y: y + dy,
        tile,
      }));

      let paintableCount = 0;
      let blockedCount = 0;

      for (const placement of placements) {
        const current = map?.[placement.y]?.[placement.x];
        const key = coordKey(placement.x, placement.y);
        if (!isPrimeVesselLabPaintableTile(current) || reserved.has(key)) {
          blockedCount += 1;
          continue;
        }
        paintableCount += 1;
      }

      if (paintableCount <= 0) continue;

      candidates.push({
        x,
        y,
        paintableCount,
        blockedCount,
      });
    }
  }

  return candidates;
}

function placePrimeLabSetpiece(map, rng, bounds, reserved) {
  const template = pick(rng, PRIME_VESSEL_LAB_SETPIECE_TEMPLATES);
  if (!template) return false;

  const candidates = collectPrimeLabSetpieceCandidates(map, bounds, template, reserved);
  if (!candidates.length) return false;

  candidates.sort((a, b) => {
    if (a.paintableCount !== b.paintableCount) return b.paintableCount - a.paintableCount;
    if (a.blockedCount !== b.blockedCount) return a.blockedCount - b.blockedCount;
    return rng() < 0.5 ? -1 : 1;
  });

  const bestPaintableCount = candidates[0]?.paintableCount ?? 0;
  const bestBlockedCount = candidates[0]?.blockedCount ?? 0;
  const best = candidates.filter(
    (entry) =>
      entry.paintableCount === bestPaintableCount &&
      entry.blockedCount === bestBlockedCount,
  );
  const chosen = pick(rng, best) || candidates[0];
  if (!chosen) return false;

  let painted = false;
  template.offsets.forEach(({ dx, dy, tile }) => {
    const x = chosen.x + dx;
    const y = chosen.y + dy;
    const current = map?.[y]?.[x];
    const key = coordKey(x, y);
    if (!isPrimeVesselLabPaintableTile(current) || reserved.has(key)) return;
    setTile(map, x, y, tile);
    reserved.add(key);
    painted = true;
  });

  return painted;
}

function placePrimeLabSetpieces(map, rng, bounds, reserved) {
  const targetCount = 2 + Math.floor(rng() * 2); // 2-3 per quadrant
  let placed = 0;

  for (let i = 0; i < targetCount; i += 1) {
    if (placePrimeLabSetpiece(map, rng, bounds, reserved)) placed += 1;
  }

  if (placed <= 0) {
    placePrimeLabSetpiece(map, rng, bounds, reserved);
  }
}

function carvePrimeConnectorNetwork(map, rng, def) {
  const rooms = [];
  const nodes = [def.start];
  const extraRooms = 2 + Math.floor(rng() * 3);

  for (let i = 0; i < extraRooms; i += 1) {
    let result = null;
    for (let tries = 0; tries < 20 && !result; tries += 1) {
      result = pickRoomCenterWithinBounds(rng, def.bounds, rooms);
    }
    if (!result) continue;
    rooms.push(result.room);
    carveRectExclusive(map, result.room.x, result.room.y, result.room.w, result.room.h, 'floor');
    nodes.push(result.center);
  }

  nodes.push(def.end);

  for (let i = 1; i < nodes.length; i += 1) {
    carveOrthogonalPath(map, nodes[i - 1], nodes[i], 'corridor');
  }

  if (nodes.length >= 4) {
    const extraA = nodes[1 + Math.floor(rng() * Math.max(1, nodes.length - 2))];
    const extraB = nodes[1 + Math.floor(rng() * Math.max(1, nodes.length - 2))];
    if (extraA && extraB && (extraA.x !== extraB.x || extraA.y !== extraB.y)) {
      carveOrthogonalPath(map, extraA, extraB, 'corridor');
    }
  }

  const occupied = new Set([coordKey(def.start.x, def.start.y), coordKey(def.end.x, def.end.y)]);
  occupied.add(coordKey(def.roomBounds.x1 + Math.floor((def.roomBounds.x2 - def.roomBounds.x1) / 2), def.roomBounds.y1 + Math.floor((def.roomBounds.y2 - def.roomBounds.y1) / 2)));
  placePrimeConnectorRewardChest(map, rng, def, occupied);
  placePrimeConnectorDecor(map, rng, def.bounds, occupied);
  placePrimeConnectorBraziers(map, rng, def.bounds, occupied);
  placePrimeLabSetpieces(map, rng, {
    x1: Math.min(def.roomBounds.x1, def.bounds.x1),
    y1: Math.min(def.roomBounds.y1, def.bounds.y1),
    x2: Math.max(def.roomBounds.x2, def.bounds.x2),
    y2: Math.max(def.roomBounds.y2, def.bounds.y2),
  }, occupied);
}


function collectPrimeVesselRoamingEnemyCandidates(map, bounds, reserved, allowedTiles = ['floor', 'corridor']) {
  const allowed = new Set(Array.isArray(allowedTiles) ? allowedTiles : ['floor', 'corridor']);
  const candidates = [];

  for (let y = bounds.y1; y <= bounds.y2; y += 1) {
    for (let x = bounds.x1; x <= bounds.x2; x += 1) {
      const tile = map?.[y]?.[x];
      if (!allowed.has(tile)) continue;
      const key = coordKey(x, y);
      if (reserved.has(key)) continue;
      candidates.push({ x, y });
    }
  }

  return candidates;
}

function placePrimeVesselRoamingEnemies(map, rng) {
  const reserved = new Set([
    coordKey(PRIME_VESSEL_PLAYER_SPAWN.x, PRIME_VESSEL_PLAYER_SPAWN.y),
    coordKey(PRIME_VESSEL_BOSS_POS.x, PRIME_VESSEL_BOSS_POS.y),
    coordKey(14, 20),
    coordKey(16, 20),
    coordKey(13, 13),
    coordKey(17, 13),
    coordKey(13, 17),
    coordKey(17, 17),
    coordKey(14, 12),
    coordKey(16, 12),
    coordKey(14, 18),
    coordKey(16, 18),
  ]);

  PRIME_VESSEL_DEVICE_COORDS.forEach(({ x, y }) => reserved.add(coordKey(x, y)));
  PRIME_VESSEL_CORE_LAB_ART.forEach(({ x, y }) => reserved.add(coordKey(x, y)));

  const groups = [
    {
      id: 'core',
      bounds: { x1: 6, y1: 11, x2: 24, y2: 22 },
      allowedTiles: ['floor', 'corridor'],
    },
    ...PRIME_VESSEL_CONNECTOR_DEFS.map((def, index) => ({
      id: `quadrant-${index}`,
      bounds: {
        x1: Math.min(def.roomBounds.x1, def.bounds.x1),
        y1: Math.min(def.roomBounds.y1, def.bounds.y1),
        x2: Math.max(def.roomBounds.x2, def.bounds.x2),
        y2: Math.max(def.roomBounds.y2, def.bounds.y2),
      },
      allowedTiles: ['floor', 'corridor'],
    })),
  ];

  const targetCount = 6 + Math.floor(rng() * 5); // 6-10 total
  let placed = 0;

  for (const group of groups) {
    if (placed >= targetCount) break;
    const candidates = collectPrimeVesselRoamingEnemyCandidates(map, group.bounds, reserved, group.allowedTiles);
    const chosen = takeRandomPrimeConnectorCandidate(rng, candidates);
    if (!chosen) continue;
    setTile(map, chosen.x, chosen.y, 'enemy');
    reserved.add(coordKey(chosen.x, chosen.y));
    placed += 1;
  }

  if (placed >= targetCount) return;

  const allCandidates = groups.flatMap((group) =>
    collectPrimeVesselRoamingEnemyCandidates(map, group.bounds, reserved, group.allowedTiles),
  );

  while (placed < targetCount && allCandidates.length > 0) {
    const chosen = takeRandomPrimeConnectorCandidate(rng, allCandidates);
    if (!chosen) break;
    const key = coordKey(chosen.x, chosen.y);
    if (reserved.has(key)) continue;
    setTile(map, chosen.x, chosen.y, 'enemy');
    reserved.add(key);
    placed += 1;
  }
}

function buildPrimeVesselBossFloorMap(runSeed = 0) {
  const map = Array.from({ length: PRIME_VESSEL_FLOOR_HEIGHT }, () =>
    Array(PRIME_VESSEL_FLOOR_WIDTH).fill('non-walkable'),
  );

  const rng = makeRng((((Number(runSeed) || 0) ^ 0x51ed270b) >>> 0) || 1);

  // Corner device rooms.
  PRIME_VESSEL_CONNECTOR_DEFS.forEach(({ roomBounds }) => {
    fillRect(map, roomBounds.x1, roomBounds.y1, roomBounds.x2, roomBounds.y2, 'floor');
  });

  // Central laboratory chamber.
  fillRect(map, 13, 13, 17, 17, 'floor');
  fillRect(map, 14, 11, 16, 12, 'floor');
  fillRect(map, 14, 18, 16, 20, 'floor');

  // Four fixed horizontal corridors.
  fillRect(map, 6, 13, 12, 13, 'corridor');
  fillRect(map, 18, 13, 24, 13, 'corridor');
  fillRect(map, 6, 17, 12, 17, 'corridor');
  fillRect(map, 18, 17, 24, 17, 'corridor');

  // Bottom approach to the center room.
  fillRect(map, 15, 18, 15, 22, 'corridor');
  setTile(map, 14, 20, 'merchant');
  setTile(map, 16, 20, 'rest');
  setTile(map, PRIME_VESSEL_PLAYER_SPAWN.x, PRIME_VESSEL_PLAYER_SPAWN.y, 'steps-up');

  PRIME_VESSEL_CONNECTOR_DEFS.forEach((def) => carvePrimeConnectorNetwork(map, rng, def));

  setTile(map, 13, 13, 'door-open');
  setTile(map, 17, 13, 'door-open');
  setTile(map, 13, 17, 'door-open');
  setTile(map, 17, 17, 'door-open');

  setTile(map, PRIME_VESSEL_BOSS_POS.x, PRIME_VESSEL_BOSS_POS.y, 'enemy-boss');
  PRIME_VESSEL_DEVICE_COORDS.forEach(({ x, y }) => setTile(map, x, y, 'enemy'));
  PRIME_VESSEL_CORE_LAB_ART.forEach(({ x, y, tile }) => setTile(map, x, y, tile));

  // A few stable support tiles near the core chamber so the layout never feels too empty.
  setTile(map, 14, 12, 'brazier');
  setTile(map, 16, 12, 'brazier');
  setTile(map, 14, 18, 'bookshelf-a');
  setTile(map, 16, 18, 'bookshelf-b');

  placePrimeVesselRoamingEnemies(map, rng);

  return map;
}

function buildBlackhandBossFloorMap() {
  const map = Array.from({ length: BLACKHAND_FLOOR_HEIGHT }, () =>
    Array(BLACKHAND_FLOOR_WIDTH).fill('non-walkable'),
  );

  // Entrance + main hall (3x30, counted from the first walkable tile after the entry).
  setTile(map, BLACKHAND_PLAYER_SPAWN.x, BLACKHAND_PLAYER_SPAWN.y, 'steps-up');
  fillRect(map, 9, 2, 11, 31, 'corridor');

  // Left merchant room at the 10th tile of the main hall.
  fillRect(map, 1, 9, 5, 13, 'floor');
  fillRect(map, 6, 11, 8, 11, 'corridor');
  setTile(map, 3, 9, 'merchant');
  setTile(map, 3, 13, 'brazier');
  setTile(map, 9, 10, 'brazier');
  setTile(map, 9, 12, 'brazier');

  // Right rest room at the 20th tile of the main hall.
  fillRect(map, 15, 19, 19, 23, 'floor');
  fillRect(map, 12, 21, 14, 21, 'corridor');
  setTile(map, 17, 23, 'rest');
  setTile(map, 17, 19, 'brazier');
  setTile(map, 11, 20, 'brazier');
  setTile(map, 11, 22, 'brazier');

  // Final narrow corridor and the contract chest.
  fillRect(map, 10, 32, 10, 41, 'corridor');
  setTile(map, 9, 31, 'brazier');
  setTile(map, 11, 31, 'brazier');
  setTile(map, 10, 39, 'trap');
  setTile(map, 10, 40, 'trap');
  setTile(map, BLACKHAND_CHEST_POS.x, BLACKHAND_CHEST_POS.y, 'chest-closed');

  return map;
}

function isBlackhandBossFloorMap(map) {
  if (!Array.isArray(map) || map.length !== BLACKHAND_FLOOR_HEIGHT) return false;
  if (!Array.isArray(map[0]) || map[0].length !== BLACKHAND_FLOOR_WIDTH) return false;

  return BLACKHAND_ROOM_CHECKS.every(({ x, y, allowed }) => allowed.includes(map?.[y]?.[x]));
}

function isPrimeVesselBossFloorMap(map) {
  if (!Array.isArray(map) || map.length !== PRIME_VESSEL_FLOOR_HEIGHT) return false;
  if (!Array.isArray(map[0]) || map[0].length !== PRIME_VESSEL_FLOOR_WIDTH) return false;

  return PRIME_VESSEL_ROOM_CHECKS.every(({ x, y, allowed }) => allowed.includes(map?.[y]?.[x]));
}

function normalizeBossKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function normalizeFloorNumber(value, fallback = SPECIAL_BOSS_FLOOR) {
  return Math.max(1, Math.floor(Number(value) || fallback));
}

function isEmpoweredSpecialBossFloor(floor) {
  const normalizedFloor = normalizeFloorNumber(floor);
  return EMPOWERED_SPECIAL_BOSS_FLOORS.includes(normalizedFloor);
}

export function isSpecialBossFloor(floor) {
  const normalizedFloor = normalizeFloorNumber(floor);
  return SPECIAL_BOSS_FLOORS.includes(normalizedFloor);
}

export function getMonsignorBossFloorMood() {
  return {
    id: 'monsignor-boss-floor',
    name: 'Cathedral Floor',
    tilesetId: 'default',
    mapSize: { mode: 'fixed', value: MONSIGNOR_FLOOR_WIDTH },
    torchDuration: {
      mode: 'default',
      value: 90,
    },
    layout: {
      preset: 'colossal',
      forceSingleRoom: true,
      noCorridors: true,
      roomSizeMultiplier: 2.45,
      roomCountMultiplier: 0.1,
      corridorDensityMultiplier: 0.0,
      corridorLengthMultiplier: 0.0,
      deadEndMultiplier: 0.0,
    },
    counts: {
      enemyMultiplier: 0,
      trapMultiplier: 0.35,
      brazierMultiplier: 1.25,
      restMultiplier: 0.45,
      chestMultiplier: 0.7,
      chestFlatBonus: 0,
      simpleChestCount: 0,
      loreMultiplier: 1.2,
      shopMultiplier: 0,
      lockedDoorMultiplier: 0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 0.85,
      statueMultiplier: 0,
      statueFlatBonus: 0,
      statueSpawnChance: 0,
    },
  };
}

export function getBlackhandBossFloorMood() {
  return {
    id: 'blackhand-boss-floor',
    name: 'Contract Passage',
    tilesetId: 'default',
    mapSize: { mode: 'fixed', value: BLACKHAND_FLOOR_WIDTH },
    torchDuration: {
      mode: 'default',
      value: 90,
    },
    layout: {
      preset: 'contract-passage',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 0.4,
      roomCountMultiplier: 0,
      corridorDensityMultiplier: 0,
      corridorLengthMultiplier: 0,
      deadEndMultiplier: 0,
    },
    counts: {
      enemyMultiplier: 0,
      trapMultiplier: 0,
      brazierMultiplier: 0,
      restMultiplier: 0,
      chestMultiplier: 0,
      chestFlatBonus: 0,
      simpleChestCount: 0,
      loreMultiplier: 0,
      shopMultiplier: 0,
      lockedDoorMultiplier: 0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 0,
      statueMultiplier: 0,
      statueFlatBonus: 0,
      statueSpawnChance: 0,
    },
  };
}

export function getPrimeVesselBossFloorMood() {
  return {
    id: 'prime-vessel-boss-floor',
    name: 'Laboratory Mood',
    tilesetId: 'default',
    mapSize: { mode: 'fixed', value: PRIME_VESSEL_FLOOR_WIDTH },
    torchDuration: {
      mode: 'default',
      value: 86,
    },
    layout: {
      preset: 'laboratory',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 1.0,
      roomCountMultiplier: 1.0,
      corridorDensityMultiplier: 1.0,
      corridorLengthMultiplier: 1.0,
      deadEndMultiplier: 1.0,
    },
    counts: {
      enemyMultiplier: 0,
      trapMultiplier: 0,
      brazierMultiplier: 0,
      restMultiplier: 0,
      chestMultiplier: 0,
      chestFlatBonus: 0,
      simpleChestCount: 0,
      loreMultiplier: 0,
      shopMultiplier: 0,
      lockedDoorMultiplier: 0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 0,
      statueMultiplier: 0,
      statueFlatBonus: 0,
      statueSpawnChance: 0,
    },
  };
}

const SPECIAL_BOSS_FLOOR_POOL = Object.freeze([
  {
    id: 'nested_horror',
    enemyId: SPIDER_BOSS_ENEMY_ID,
    name: 'Nested Horror',
    weight: 1,
    spawnMode: 'spider-system',
    usesSpiderBossSystem: true,
    getMood: getSpiderBossFloorMood,
  },
  {
    id: 'monsignor',
    enemyId: MONSIGNOR_BOSS_ENEMY_ID,
    name: 'The Monsignor',
    weight: 1,
    spawnMode: 'static-boss-tile',
    usesSpiderBossSystem: false,
    getMood: getMonsignorBossFloorMood,
  },
  {
    id: 'prime_vessel',
    enemyId: PRIME_VESSEL_BOSS_ENEMY_ID,
    name: 'The Prime Vessel',
    weight: 1,
    spawnMode: 'static-boss-tile',
    usesSpiderBossSystem: false,
    getMood: getPrimeVesselBossFloorMood,
  },
  {
    id: 'blackhand',
    enemyId: BLACKHAND_BOSS_ENEMY_ID,
    name: 'The Blackhand',
    weight: 1,
    spawnMode: 'scripted-ambush',
    usesSpiderBossSystem: false,
    getMood: getBlackhandBossFloorMood,
  },
]);

export const TEST_RUN_SPECIAL_BOSS_OVERRIDE_STORAGE_KEY = 'dd_test_run_floor4_boss_override_v1';

export const TEST_RUN_SPECIAL_BOSS_OPTIONS = Object.freeze([
  { id: '', name: 'Random (weights)' },
  ...SPECIAL_BOSS_FLOOR_POOL.map((entry) => ({
    id: String(entry.id ?? '').trim(),
    name: String(entry.name ?? entry.id ?? '').trim(),
  })),
]);

function readTestRunSpecialBossOverrideId() {
  if (typeof window === 'undefined' || !window.localStorage) return '';
  try {
    return String(
      window.localStorage.getItem(TEST_RUN_SPECIAL_BOSS_OVERRIDE_STORAGE_KEY) ?? ''
    ).trim();
  } catch {
    return '';
  }
}

function getSpecialBossDefByKey(value) {
  const key = normalizeBossKey(value);
  if (!key) return null;

  return (
    SPECIAL_BOSS_FLOOR_POOL.find(
      (entry) =>
        normalizeBossKey(entry.id) === key ||
        normalizeBossKey(entry.enemyId) === key,
    ) || null
  );
}

function cloneSelection(def, { floor = SPECIAL_BOSS_FLOOR } = {}) {
  if (!def) return null;

  const normalizedFloor = normalizeFloorNumber(floor);
  const isEmpoweredEncounter = isEmpoweredSpecialBossFloor(normalizedFloor);

  return {
    id: def.id,
    enemyId: def.enemyId,
    name: def.name,
    floor: normalizedFloor,
    spawnMode: def.spawnMode,
    usesSpiderBossSystem: !!def.usesSpiderBossSystem,
    moodId: def.getMood?.()?.id ?? null,
    hpMultiplier: isEmpoweredEncounter ? EMPOWERED_SPECIAL_BOSS_HP_MULTIPLIER : 1,
    armorMultiplier: isEmpoweredEncounter ? EMPOWERED_SPECIAL_BOSS_ARMOR_MULTIPLIER : 1,
    isEmpoweredEncounter,
  };
}

export function getSpecialBossFloorEncounter(selectionOrId, { floor, runSeed } = {}) {
  const directDef = getSpecialBossDefByKey(
    selectionOrId?.id ?? selectionOrId?.enemyId ?? selectionOrId,
  );

  if (directDef) {
    return cloneSelection(directDef, {
      floor:
        selectionOrId && typeof selectionOrId === 'object'
          ? selectionOrId.floor ?? floor
          : floor,
    });
  }

  return pickSpecialBossFloorEncounter({ floor, runSeed });
}

export function getSpecialBossEnemyId(selectionOrId) {
  return getSpecialBossFloorEncounter(selectionOrId)?.enemyId ?? null;
}

export function isSpiderBossEncounterSelection(selectionOrId) {
  return getSpecialBossEnemyId(selectionOrId) === SPIDER_BOSS_ENEMY_ID;
}

export function getSpecialBossFloorMood(selectionOrId) {
  const def = getSpecialBossDefByKey(
    selectionOrId?.id ?? selectionOrId?.enemyId ?? selectionOrId,
  );
  return def?.getMood?.() ?? null;
}

export function getSpecialBossCombatOverrides(selectionOrId) {
  const selection = getSpecialBossFloorEncounter(selectionOrId, {
    floor: selectionOrId?.floor,
  });
  if (!selection) return null;

  const hpMultiplier = Math.max(0, Number(selection.hpMultiplier ?? 1) || 1);
  const armorMultiplier = Math.max(0, Number(selection.armorMultiplier ?? 1) || 1);

  if (hpMultiplier === 1 && armorMultiplier === 1) return null;

  return {
    hpMultiplier,
    armorMultiplier,
  };
}

function pickWeightedBossFromPool(pool, { floor, runSeed, salt = 0x6f0b913d } = {}) {
  if (!Array.isArray(pool) || pool.length === 0) return null;

  const totalWeight = pool.reduce(
    (sum, entry) => sum + Math.max(0, Number(entry.weight ?? SPECIAL_BOSS_WEIGHT_FALLBACK) || 0),
    0,
  );

  if (!(totalWeight > 0)) {
    return pool[0] ?? null;
  }

  const normalizedFloor = normalizeFloorNumber(floor);
  const rng = makeRng(
    ((Number(runSeed) || 0) ^ ((normalizedFloor * 0x9e3779b9) >>> 0) ^ salt) >>> 0,
  );

  let roll = rng() * totalWeight;
  for (const entry of pool) {
    roll -= Math.max(0, Number(entry.weight ?? SPECIAL_BOSS_WEIGHT_FALLBACK) || 0);
    if (roll <= 0) return entry;
  }

  return pool[pool.length - 1] ?? null;
}

const SPECIAL_BOSS_FLOOR_SALT_BY_FLOOR = Object.freeze({
  [SPECIAL_BOSS_FLOOR]: 0x6f0b913d,
  [SECOND_SPECIAL_BOSS_FLOOR]: 0x51ed270b,
  [THIRD_SPECIAL_BOSS_FLOOR]: 0x2d4f91c7,
  [FOURTH_SPECIAL_BOSS_FLOOR]: 0x14b8c5ef,
});

export function pickSpecialBossFloorEncounter({ floor, runSeed } = {}) {
  const normalizedFloor = normalizeFloorNumber(floor);
  if (!isSpecialBossFloor(normalizedFloor)) return null;

  const weightedPool = SPECIAL_BOSS_FLOOR_POOL.filter(
    (entry) => Number(entry.weight ?? SPECIAL_BOSS_WEIGHT_FALLBACK) > 0,
  );

  if (!weightedPool.length) return null;

  const forcedOverride = getSpecialBossDefByKey(readTestRunSpecialBossOverrideId());

  const priorSpecialFloors = SPECIAL_BOSS_FLOORS.filter(
    (bossFloor) => bossFloor < normalizedFloor,
  );
  const excludedBossKeys = new Set(
    priorSpecialFloors
      .map((bossFloor) =>
        pickSpecialBossFloorEncounter({
          floor: bossFloor,
          runSeed,
        }),
      )
      .map((selection) => normalizeBossKey(selection?.id ?? selection?.enemyId))
      .filter(Boolean),
  );

  const remainingPool = weightedPool.filter(
    (entry) => !excludedBossKeys.has(normalizeBossKey(entry.id)),
  );
  if (!remainingPool.length) return null;

  if (forcedOverride) {
    const forcedRemaining = remainingPool.find(
      (entry) => normalizeBossKey(entry.id) === normalizeBossKey(forcedOverride.id),
    );
    if (forcedRemaining) {
      return cloneSelection(forcedRemaining, { floor: normalizedFloor });
    }
  }

  const picked = pickWeightedBossFromPool(remainingPool, {
    floor: normalizedFloor,
    runSeed,
    salt: SPECIAL_BOSS_FLOOR_SALT_BY_FLOOR[normalizedFloor] ?? 0x6f0b913d,
  });
  return cloneSelection(picked, { floor: normalizedFloor });
}

function sanitizeStaticBossFloorMap(map) {
  if (!Array.isArray(map) || map.length === 0) return map;

  const height = map.length;
  const width = map[0]?.length || 0;
  if (width <= 0) return map;

  let changed = false;
  const next = map.map((row) => row.slice());

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const tile = next[y]?.[x];
      const isBoundary = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      let replacement = tile;

      if (isBoundary) {
        replacement = tile === 'steps-up' ? 'steps-up' : 'non-walkable';
      } else if (tile === 'steps-up' || tile === 'enemy-boss') {
        replacement = tile;
      } else if (isWalkable(tile) && tile !== 'merchant' && tile !== 'steps-down') {
        replacement = 'floor';
      } else {
        replacement = 'floor';
      }

      if (replacement !== tile) {
        next[y][x] = replacement;
        changed = true;
      }
    }
  }

  return changed ? next : map;
}

export function isMonsignorBossEncounterSelection(selectionOrId) {
  return getSpecialBossEnemyId(selectionOrId) === MONSIGNOR_BOSS_ENEMY_ID;
}

export function isPrimeVesselBossEncounterSelection(selectionOrId) {
  return getSpecialBossEnemyId(selectionOrId) === PRIME_VESSEL_BOSS_ENEMY_ID;
}

export function isBlackhandBossEncounterSelection(selectionOrId) {
  return getSpecialBossEnemyId(selectionOrId) === BLACKHAND_BOSS_ENEMY_ID;
}

export function getSpecialBossFloorPlayerSpawn(selectionOrId) {
  if (isMonsignorBossEncounterSelection(selectionOrId)) return { ...MONSIGNOR_PLAYER_SPAWN };
  if (isPrimeVesselBossEncounterSelection(selectionOrId)) return { ...PRIME_VESSEL_PLAYER_SPAWN };
  if (isBlackhandBossEncounterSelection(selectionOrId)) return { ...BLACKHAND_PLAYER_SPAWN };
  return null;
}

export function getSpecialBossFloorInitialLitBraziers(selectionOrId) {
  if (!isMonsignorBossEncounterSelection(selectionOrId)) return null;
  return new Set(MONSIGNOR_LIT_BRAZIER_KEYS);
}

export function getSpecialBossFloorEnemyIdAtTile(selectionOrId, { x, y, tile } = {}) {
  if (isMonsignorBossEncounterSelection(selectionOrId)) {
    if (tile !== 'enemy') return null;
    return MONSIGNOR_GUARD_KEYS.has(coordKey(x, y)) ? MONSIGNOR_GUARD_ENEMY_ID : null;
  }

  if (isPrimeVesselBossEncounterSelection(selectionOrId)) {
    if (tile !== 'enemy') return null;
    return PRIME_VESSEL_DEVICE_ID_BY_KEY.get(coordKey(x, y)) ?? LABORATORY_FLOOR_ROAMING_ENEMY_ID;
  }

  if (isBlackhandBossEncounterSelection(selectionOrId)) {
    return null;
  }

  return null;
}

function chooseStaticBossSpawnPosition(map, playerPos) {
  if (!Array.isArray(map) || map.length === 0) return null;

  const width = map[0]?.length || 0;
  const height = map.length;
  const centerX = (width - 1) / 2;
  const candidates = [];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const tile = map[y]?.[x];
      if (!isWalkable(tile)) continue;
      if (tile === 'steps-up' || tile === 'steps-down') continue;
      if (tile === 'merchant' || tile === 'enemy' || tile === 'enemy-boss') continue;
      if (tile === 'hidden-corridor' || tile === 'secret-entrance') continue;

      const playerDistance = playerPos
        ? Math.max(Math.abs(x - playerPos.x), Math.abs(y - playerPos.y))
        : 0;

      candidates.push({
        x,
        y,
        playerDistance,
        topBias: y,
        centerBias: Math.abs(x - centerX),
      });
    }
  }

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    if (a.playerDistance !== b.playerDistance) return b.playerDistance - a.playerDistance;
    if (a.topBias !== b.topBias) return a.topBias - b.topBias;
    if (a.centerBias !== b.centerBias) return a.centerBias - b.centerBias;
    if (a.x !== b.x) return a.x - b.x;
    return a.y - b.y;
  });

  return { x: candidates[0].x, y: candidates[0].y };
}

export function countMonsignorAliveGuards(map) {
  if (!Array.isArray(map) || !map.length) return 0;

  return MONSIGNOR_GUARD_COORDS.reduce((count, { x, y }) => {
    return count + (map?.[y]?.[x] === 'enemy' ? 1 : 0);
  }, 0);
}

export function countMonsignorDefeatedGuards(map) {
  return Math.max(0, MONSIGNOR_GUARD_COORDS.length - countMonsignorAliveGuards(map));
}

export function getMonsignorBossHpReduction(map) {
  return countMonsignorDefeatedGuards(map) * MONSIGNOR_BOSS_HP_REDUCTION_PER_GUARD;
}

export function countPrimeVesselAliveDevices(map) {
  if (!Array.isArray(map) || !map.length) return 0;

  return PRIME_VESSEL_DEVICE_COORDS.reduce((count, { x, y }) => {
    return count + (map?.[y]?.[x] === 'enemy' ? 1 : 0);
  }, 0);
}

export function countPrimeVesselDefeatedDevices(map) {
  return Math.max(0, PRIME_VESSEL_DEVICE_COORDS.length - countPrimeVesselAliveDevices(map));
}

export function getPrimeVesselBossHpReduction(map) {
  return countPrimeVesselDefeatedDevices(map) * PRIME_VESSEL_DEVICE_HP_REDUCTION;
}

export function getPrimeVesselCombatStatusMods(map) {
  const isAliveAt = (x, y) => map?.[y]?.[x] === 'enemy';

  return {
    primeVesselPulseEngineActive: isAliveAt(27, 3),
    primeVesselSynapticRetortActive: isAliveAt(3, 27),
    primeVesselAstralReliquaryActive: isAliveAt(27, 27),
    primeVesselPrimeCrucibleActive: isAliveAt(3, 3),
  };
}

export function liberateMonsignorGuards(map) {
  if (!Array.isArray(map) || !map.length) return map;

  const next = map.map((row) => row.slice());

  MONSIGNOR_GUARD_COORDS.forEach(({ x, y }) => {
    if (!next?.[y] || next[y][x] == null || next[y][x] === 'non-walkable') return;
    next[y][x] = MONSIGNOR_LIBERATED_GUARD_TILE;
  });

  return next;
}

export function ensureSelectedSpecialBossFloorMap({ map, selection, playerPos, runSeed = 0 }) {
  const encounter = getSpecialBossFloorEncounter(selection);
  if (!encounter || encounter.usesSpiderBossSystem) return map;

  if (isMonsignorBossEncounterSelection(encounter)) {
    return isMonsignorBossFloorMap(map) ? map : buildMonsignorBossFloorMap();
  }

  if (isPrimeVesselBossEncounterSelection(encounter)) {
    return isPrimeVesselBossFloorMap(map) ? map : buildPrimeVesselBossFloorMap(runSeed);
  }

  if (isBlackhandBossEncounterSelection(encounter)) {
    return isBlackhandBossFloorMap(map) ? map : buildBlackhandBossFloorMap();
  }

  const sanitized = sanitizeStaticBossFloorMap(map);
  if (!Array.isArray(sanitized) || sanitized.length === 0) return sanitized;

  for (let y = 0; y < sanitized.length; y++) {
    for (let x = 0; x < (sanitized[y]?.length || 0); x++) {
      if (sanitized[y]?.[x] === 'enemy-boss') return sanitized;
    }
  }

  const spawnPos = chooseStaticBossSpawnPosition(sanitized, playerPos);
  if (!spawnPos || !sanitized[spawnPos.y]) return sanitized;

  const next = sanitized === map ? sanitized.map((row) => row.slice()) : sanitized;
  next[spawnPos.y][spawnPos.x] = 'enemy-boss';
  return next;
}
